import type { DiagnosticReport } from '@medplum/fhirtypes';

import { Caption, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { emptySectionCopy } from '../../core/copy';
import type { FhirView, ViewProps } from '../../core/view-contract';
import { formatFhirDate } from '../observation/format';

/**
 * The exported DiagnosticReport component. Result observations arrive
 * pre-rendered; the read view re-containers them from `slotItems.result`
 * into a divided list so rows share the card's gutters.
 */
export const DiagnosticReportView: FhirView<DiagnosticReport> = (props) => {
  // Nested under another resource → summarize; the parent owns the layout.
  if (props.ctx.parent) return <DiagnosticReportCompactView {...props} />;
  return (
    <DiagnosticReportReadView
      {...props}
      condensed={props.ctx.placement === 'side-panel'}
    />
  );
};

function reportName(data: DiagnosticReport): string {
  return (
    data.code?.text ?? data.code?.coding?.[0]?.display ?? 'Diagnostic report'
  );
}

function reportDateText(data: DiagnosticReport): string | undefined {
  return formatFhirDate(
    data.effectiveDateTime ?? data.effectivePeriod?.start ?? data.issued,
  );
}

function performerText(data: DiagnosticReport): string | undefined {
  const names = (data.performer ?? [])
    .map((performer) => performer.display)
    .filter(Boolean);
  return names.length > 0 ? names.join(', ') : undefined;
}

function DiagnosticReportReadView({
  data,
  slotItems,
  condensed = false,
}: ViewProps<DiagnosticReport> & { condensed?: boolean }) {
  const date = reportDateText(data);
  const performer = performerText(data);
  const results = slotItems.result;
  const pad = condensed ? 'px-3 py-2' : 'px-4 py-3';
  const gutter = condensed ? 'px-3' : 'px-4';
  return (
    <section
      data-view="diagnostic-report-read"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <header
        className={`border-b border-border-primary ${pad} ${
          condensed
            ? ''
            : 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1'
        }`}
      >
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text
            inline
            size={condensed ? 'sm' : 'md'}
            weight="semibold"
            className="truncate"
          >
            {reportName(data)}
          </Text>
          <FhirStatusTag status={data.status} />
        </Stack>
        <div
          className={
            condensed
              ? 'mt-0.5 flex items-center gap-3'
              : 'flex items-center gap-3'
          }
        >
          {performer && (
            <Text inline size="xs" color="tertiary" className="truncate">
              {performer}
            </Text>
          )}
          {date && (
            <Text inline size="xs" color="tertiary">
              {date}
            </Text>
          )}
        </div>
      </header>
      {results && results.length > 0 ? (
        <div className={`divide-y divide-border-primary ${gutter} py-1`}>
          {results}
        </div>
      ) : (
        <Text size="sm" color="tertiary" className={pad}>
          {emptySectionCopy('results')}
        </Text>
      )}
      {data.conclusion && (
        <footer className={`border-t border-border-primary ${pad}`}>
          <Caption color="tertiary">Conclusion</Caption>
          <Text size="sm" weight="regular" className="mt-1">
            {data.conclusion}
          </Text>
        </footer>
      )}
    </section>
  );
}

const DiagnosticReportCompactView: FhirView<DiagnosticReport> = ({ data }) => (
  <span
    data-view="diagnostic-report-compact"
    className="inline-flex items-center gap-2 rounded-full border border-border-primary px-3 py-1"
  >
    <Text inline size="xs">
      {reportName(data)}
    </Text>
    <FhirStatusTag status={data.status} />
  </span>
);
